import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import {
  caregiverServiceAreaService,
  ServiceArea,
} from '../../services/caregiverServiceAreaService';
import {
  premiumPlacementService,
  PremiumPlacement,
} from '../../services/premiumPlacementService';

// FEAT-065 (MVP-18.7): caregiver premium placement purchase UI.

const PremiumPlacementPage: React.FC = () => {
  const [areas, setAreas] = useState<ServiceArea[]>([]);
  const [placements, setPlacements] = useState<PremiumPlacement[]>([]);
  const [loading, setLoading] = useState(true);
  const [checkingOutId, setCheckingOutId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [serviceAreas, activePlacements] = await Promise.all([
        caregiverServiceAreaService.getServiceAreas(),
        premiumPlacementService.listPlacements(),
      ]);
      setAreas(serviceAreas.selected);
      setPlacements(activePlacements);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'We had trouble loading premium placement options.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCheckout = async (serviceAreaId: string) => {
    setCheckingOutId(serviceAreaId);
    setError(null);
    setSuccess(null);
    try {
      const result = await premiumPlacementService.checkout(serviceAreaId);
      setSuccess('Premium placement activated for your selected area.');
      window.open(result.checkoutUrl, '_blank', 'noopener,noreferrer');
      await load();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Premium checkout could not be completed.',
      );
    } finally {
      setCheckingOutId(null);
    }
  };

  const activeZipSet = new Set(placements.map((p) => p.zip));

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Stack spacing={2} sx={{ mb: 3 }}>
        <Typography variant="h4">Premium Area Placement</Typography>
        <Typography variant="body1" color="text.secondary">
          Boost visibility in the zip codes you serve. Premium caregivers appear
          higher in area discovery results.
        </Typography>
      </Stack>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress data-testid="premium-placement-loading" />
        </Box>
      )}

      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}

      {!loading && areas.length === 0 && (
        <Alert severity="info" data-testid="premium-placement-no-areas">
          Select service areas before purchasing premium placement.
        </Alert>
      )}

      <Stack spacing={2} data-testid="premium-placement-list">
        {areas.map((area) => {
          const isActive = activeZipSet.has(area.zip);
          return (
            <Card key={area.id}>
              <CardContent>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography variant="h6">
                      {area.zip}
                      {area.city ? ` — ${area.city}` : ''}
                    </Typography>
                    {isActive && (
                      <Chip
                        icon={<StarIcon />}
                        label="Active premium"
                        color="warning"
                        size="small"
                        sx={{ mt: 1 }}
                        data-testid={`premium-active-${area.zip}`}
                      />
                    )}
                  </Box>
                  <Button
                    variant="contained"
                    disabled={isActive || checkingOutId === area.id}
                    onClick={() => void handleCheckout(area.id)}
                    data-testid={`premium-checkout-${area.zip}`}
                  >
                    {checkingOutId === area.id ? 'Processing…' : 'Purchase premium'}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          );
        })}
      </Stack>
    </Container>
  );
};

export default PremiumPlacementPage;
